/* eslint-disable react-hooks/exhaustive-deps */
import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useEffect, useState } from "react";
import {
  fetchAdminUnitDetail,
  fetchChildAdminUnits,
} from "services/advocateComplaintService";
import {
  editComplaint,
  fetchAssignedProsecutor,
} from "services/prosecutorDecipilineService";
import { AdminUnit, AssignedProsecutor, Complaints, Prosecutor } from "types";
import { LoadingIcon } from "utils/constant";
import {
  COMPLIANT_STATUS,
  DECISION_TYPE,
  DECISION_TYPE2,
  HIERARCHY_LEVELS,
} from "utils/constantValues";

interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: Complaints;
  reassing?: boolean;
  onRefresh: any;
}

export default function AddComplaintDecisionModal({
  isOpen,
  setIsOpen,
  complaintData,
  reassing,
  onRefresh,
}: ModalProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [prosecutorsData, setProsecutorsData] = useState<AssignedProsecutor[]>(
    [],
  );

  const [formState, setFormState] = useState({
    decision_type: complaintData?.decision_type || DECISION_TYPE?.APPROVED,
    decision_summary: complaintData?.decision_summary || "",
    admin_unit_id: "",
    decision_date: new Date(),
    assigned_date: new Date().toISOString().split("T")[0],
    assigned_prosecutor_id: complaintData?.accused_prosecutor_id || "",
    assigned_prosecutor: null as Prosecutor | null,
    complaint: null,
    files: [] as File[],
  });
  const [adminUnitData, setAdminUnitData] = useState<AdminUnit>();
  const [childAdminUnit, setChildAdminUnit] = useState<any>([]);

  useEffect(() => {
    const loadProsecutors = async () => {
      try {
        const data = await fetchAssignedProsecutor(
          user?.admin_unit,
          user?.org_unit,
          user?.access,
        );
        setProsecutorsData(
          data?.filter((a) => a?.roles?.includes("InspectionProsecutor")) || [],
        );
      } catch (err) {
        console.error("Failed to load prosecutors:", err);
      }
    };
    loadProsecutors();
  }, [user]);

  useEffect(() => {
    const loadAdminUnitDetail = async () => {
      try {
        const adminUnitDetail = await fetchAdminUnitDetail(
          user?.admin_unit,
          user?.access,
        );
        setAdminUnitData(adminUnitDetail);
      } catch (err) {
        console.error("Failed to load admin unit detail:", err);
      }
    };
    loadAdminUnitDetail();
  }, [user]);

  useEffect(() => {
    const loadChildAdminUnit = async () => {
      try {
        const adminUnitDetail = await fetchChildAdminUnits(
          adminUnitData?.uu_id || "",
          user?.access,
        );
        setChildAdminUnit(adminUnitDetail?.units);
      } catch (err) {
        console.error("Failed to load child admin units:", err);
      } finally {
        setLoading(false);
      }
    };
    adminUnitData?.uu_id && loadChildAdminUnit();
  }, [adminUnitData]);

  const filtered = prosecutorsData?.filter((p) =>
    `${p?.user?.first_name || ""} ${p?.user?.last_name || ""}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "decision_type") {
      setFormState((prev) => ({ ...prev, assigned_prosecutor: null }));
    }

    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (prosecutor: Prosecutor, name: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: prosecutor,
    }));
    setQuery("");
  };

  const getDecisionLabel = () => {
    if (formState?.decision_type === DECISION_TYPE2.JURISDICTION) {
      if (adminUnitData?.hierarchy_level?.name === HIERARCHY_LEVELS.REGIONAL) {
        return t("forwardToZonalAdministration");
      } else if (
        adminUnitData?.hierarchy_level?.name === HIERARCHY_LEVELS.ZONAL
      ) {
        return t("forwardToRegionalAdministration");
      }
    } else if (formState?.decision_type === DECISION_TYPE2.APPROVED) {
      return t("approveAndAssignInvestigator");
    } else if (formState?.decision_type === DECISION_TYPE2.REJECTED) {
      return t("rejectComplaint");
    } else if (formState?.decision_type === DECISION_TYPE2.MORE_INFORMATION) {
      return t("requestAdditionalInformation");
    }
    return t("submitDecision");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      formState?.decision_type === DECISION_TYPE?.APPROVED &&
      !formState?.assigned_prosecutor?.id
    ) {
      showToast(t("investigatorAssignmentRequiredForApproval"), "error");
      return;
    }

    if (
      formState?.decision_type === DECISION_TYPE2.JURISDICTION &&
      adminUnitData?.hierarchy_level?.name === HIERARCHY_LEVELS.REGIONAL &&
      !formState?.admin_unit_id
    ) {
      showToast(t("pleaseSelectZoneForForwarding"), "error");
      return;
    }

    setLoading(true);

    try {
      await editComplaint({
        ...complaintData,
        ...formState,
        admin_unit_id:
          formState?.decision_type === DECISION_TYPE2.JURISDICTION &&
          adminUnitData?.hierarchy_level?.name === HIERARCHY_LEVELS.REGIONAL
            ? formState?.admin_unit_id
            : formState?.decision_type === DECISION_TYPE2.JURISDICTION &&
                adminUnitData?.hierarchy_level?.name === HIERARCHY_LEVELS.ZONAL
              ? adminUnitData?.parent_unit || complaintData?.admin_unit_id
              : complaintData?.admin_unit_id,
        out_of_jursdicition_source:
          formState?.decision_type === DECISION_TYPE2.JURISDICTION &&
          adminUnitData?.hierarchy_level?.name === HIERARCHY_LEVELS.REGIONAL
            ? childAdminUnit?.find(
                (obj: any) =>
                  obj.uu_id?.toString() ===
                  formState?.admin_unit_id?.toString(),
              )?.unit_name
            : "",
        user_id_assigned_prosecutor: formState?.assigned_prosecutor
          ? formState?.assigned_prosecutor?.id
          : "",
        assigned_prosecutor: `${
          formState?.assigned_prosecutor?.first_name || ""
        } ${formState?.assigned_prosecutor?.last_name || ""}`,
        status:
          formState?.decision_type === DECISION_TYPE2.APPROVED
            ? COMPLIANT_STATUS?.UNDER_INVESTIGATION
            : formState?.decision_type === DECISION_TYPE2.REJECTED
              ? COMPLIANT_STATUS.REJECTED
              : formState?.decision_type === DECISION_TYPE2.JURISDICTION
                ? COMPLIANT_STATUS.FORWARDED
                : formState?.decision_type === DECISION_TYPE2.MORE_INFORMATION
                  ? COMPLIANT_STATUS?.MORE_INFORMATION_REQUESTED
                  : COMPLIANT_STATUS?.CLOSED,
        transferred_from_admin_unit_id: adminUnitData?.unit_name,
      });

      onRefresh();
      setIsOpen(false);
      showToast(t("decisionOfficiallyRecordedInSystem"), "success");
      setFormState({
        assigned_prosecutor_id: "",
        decision_type: DECISION_TYPE?.APPROVED,
        decision_summary: "",
        admin_unit_id: "",
        assigned_date: new Date().toISOString().split("T")[0],
        decision_date: new Date(),
        assigned_prosecutor: null,
        complaint: null,
        files: [] as File[],
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("failedToProcessAdministrativeDecision"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("administrativeReviewDecision")}
    >
      <div className="mb-4">
        <p className="text-sm text-gray-500">
          {t("officialCaseManagementDetermination")}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Decision Type Section */}
        <section className="space-y-4">
          <div className="flex items-center">
            <div className="h-5 w-1 bg-red-700 rounded-r mr-3"></div>
            <h3 className="text-base font-semibold text-gray-800">
              {t("administrativeDetermination")}
            </h3>
          </div>

          <div className="space-y-4 pl-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("decisionType")} <span className="text-red-600">*</span>
              </label>
              <select
                disabled={reassing}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                name="decision_type"
                value={formState.decision_type}
                onChange={handleChange}
              >
                <option value="" disabled>
                  {t("selectAdministrativeDecision")}
                </option>
                {Object.values(DECISION_TYPE2)?.map((list) => (
                  <option key={list} value={list}>
                    {list}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {t("nextCourseOfActionForCase")}
              </p>
            </div>

            {/* Jurisdiction Transfer */}
            {formState?.decision_type === DECISION_TYPE2.JURISDICTION &&
              adminUnitData?.hierarchy_level?.name ===
                HIERARCHY_LEVELS?.REGIONAL && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("forwardToZone")} <span className="text-red-600">*</span>
                  </label>
                  <select
                    disabled={reassing}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    name="admin_unit_id"
                    value={formState.admin_unit_id}
                    onChange={handleChange}
                  >
                    <option value="">{t("selectZoneAdministration")}</option>
                    {childAdminUnit?.map((list: any) => (
                      <option key={list.uu_id} value={list.uu_id}>
                        {list?.unit_name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("caseWillBeTransferredToSelectedZonalAdministration")}
                  </p>
                </div>
              )}

            {/* Decision Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("decisionDate")} <span className="text-red-600">*</span>
              </label>
              <input
                disabled={reassing}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                type="date"
                name="assigned_date"
                value={formState.assigned_date}
                onChange={handleChange}
              />
            </div>

            {/* Decision Summary */}
            <div>
              {/* <label className="block text-sm font-medium text-gray-700 mb-1">
                Official Decision Summary <span className="text-red-600">*</span>
              </label> */}
              <textarea
                disabled={reassing}
                rows={4}
                name="decision_summary"
                value={formState.decision_summary}
                onChange={handleChange}
                placeholder={t(
                  "provideOfficialReasoningForAdministrativeDecision",
                )}
                className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-y"
              />
              {/* <p className="text-xs text-gray-500 mt-1">
                This summary will be recorded as the official justification
              </p> */}
            </div>

            {/* Prosecutor Assignment */}
            {formState?.decision_type === DECISION_TYPE?.APPROVED && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("assignInvestigator")}{" "}
                  <span className="text-red-600">*</span>
                </label>
                <select
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  name="assigned_prosecutor_id"
                  value={formState.assigned_prosecutor_id}
                  onChange={(e) => {
                    handleChange(e);
                    const selected = filtered?.find(
                      (p) =>
                        p.user.id?.toString() === e.target.value?.toString(),
                    );

                    if (selected && selected.user) {
                      handleSelect(selected.user, "assigned_prosecutor");
                    }
                  }}
                >
                  <option value="">{t("selectPublicProsecutor")}</option>
                  {filtered?.map((p) => (
                    <option key={p.user.id} value={p.user.id}>
                      {p.user?.first_name || ""} {p.user?.last_name || ""}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {t("assignedProsecutorWillConductOfficialInvestigation")}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Action Buttons */}
        <div className="pt-4 border-t border-gray-200">
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors"
            >
              {t("cancelReview")}
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                !formState.decision_summary ||
                !formState.assigned_date ||
                (formState?.decision_type === DECISION_TYPE2.JURISDICTION &&
                  adminUnitData?.hierarchy_level?.name ===
                    HIERARCHY_LEVELS.REGIONAL &&
                  !formState.admin_unit_id) ||
                (formState?.decision_type === DECISION_TYPE?.APPROVED &&
                  !formState.assigned_prosecutor_id)
              }
              className={`flex-1 px-4 py-2.5 text-white text-sm font-medium rounded-md transition-colors flex items-center justify-center space-x-2 ${
                loading ||
                !formState.decision_summary ||
                !formState.assigned_date ||
                (formState?.decision_type === DECISION_TYPE2.JURISDICTION &&
                  adminUnitData?.hierarchy_level?.name ===
                    HIERARCHY_LEVELS.REGIONAL &&
                  !formState.admin_unit_id) ||
                (formState?.decision_type === DECISION_TYPE?.APPROVED &&
                  !formState.assigned_prosecutor_id)
                  ? "bg-red-400 cursor-not-allowed"
                  : "bg-red-700 hover:bg-red-800"
              }`}
            >
              {loading ? (
                <>
                  {LoadingIcon}
                  <span>{t("processingDecision")}</span>
                </>
              ) : (
                getDecisionLabel()
              )}
            </button>
          </div>
          {/* <p className="text-xs text-gray-500 text-center mt-3">
            This decision will be permanently recorded in the case management system.
          </p> */}
        </div>
      </form>
    </SideModal>
  );
}
